import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled/macro';
import { Typography } from '@deeptrue-frontend/ui';
import { getTextWidth } from '../utils/getTextWidth';
import { getTranslation, LocaleType } from '../utils/getTranslations';
import Tooltip from '../Components/TooltipSimple';
import { ElementType } from './StackedBarChart';

interface BarProps {
  color: string;
  position: string;
}
const ChartElement = styled.div<{ width: number }>`
  width: ${({ width }) => (width === 0 ? '0.3rem' : `${width}%`)};
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 4rem;
`;
const Label = styled.div`
  display: flex;
  margin-top: 0.625rem;
`;

const Bar = styled.div<{ props: BarProps }>`
  background-color: ${({ props }) => props.color};
  height: 1rem;
  margin: 0 1px;
  border: ${({ props }) =>
    props.color === '#FFFFFF' ? '1px solid #C7CAD1' : null};
  border-top-left-radius: ${({ props }) =>
    props.position === 'first' ? '2rem' : null};
  border-bottom-left-radius: ${({ props }) =>
    props.position === 'first' ? '2rem' : null};
  border-top-right-radius: ${({ props }) =>
    props.position === 'last' ? '2rem' : null};
  border-bottom-right-radius: ${({ props }) =>
    props.position === 'last' ? '2rem' : null};
`;

interface BarElementPropsType {
  element: ElementType;
  position: string;
  locale: LocaleType;
}

const BarElement = ({ element, position, locale }: BarElementPropsType) => {
  const barRef = useRef<HTMLDivElement>(null);
  const [barWidth, setBarWidth] = useState(0);
  const [fullLabelWidth, setFullLabelWidth] = useState(0);
  const [shortLabelWidth, setShortLabelWidth] = useState(0);
  const [labelType, setLabelType] = useState<string | null>(null);
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  const mainLabel = element.sentimentMap
    ? getTranslation(element.sentimentMap, locale)
    : getTranslation(element.label, locale);

  useEffect(() => {
    if (barRef.current) {
      setBarWidth(barRef?.current?.offsetWidth);
    }

    setFullLabelWidth(
      getTextWidth(element.value.toString() + '%') + getTextWidth(mainLabel)
    );
    setShortLabelWidth(getTextWidth(element.value.toString() + '%'));

    if (fullLabelWidth + 10 < barWidth) {
      setLabelType('full');
    } else {
      if (shortLabelWidth < barWidth) {
        setLabelType('short');
      }
    }
  }, [element.value, barWidth, fullLabelWidth, mainLabel, shortLabelWidth]);

  return (
    <ChartElement width={element.value}>
      <Bar
        ref={barRef}
        props={{
          color: element?.color ? element.color : 'transparent',
          position: position,
        }}
        onMouseOver={() => setTooltipVisible(!isTooltipVisible)}
        onMouseOut={() => setTooltipVisible(!isTooltipVisible)}
      />
      <Tooltip
        title={`${element.value.toString()}%`}
        subtitle={mainLabel}
        isVisible={isTooltipVisible}
        position={labelType === 'full' ? 'bottom' : 'top'}
      />

      <Label>
        {labelType === 'short' && (
          <Typography
            fontWeight={700}
            fontSize={'0.875rem'}
            marginRight={'0.25rem'}
          >
            {element.value + '%'}
          </Typography>
        )}
        {labelType === 'full' && (
          <>
            <Typography
              fontWeight={700}
              fontSize={'0.875rem'}
              marginRight={'0.25rem'}
            >
              {element.value + '%'}
            </Typography>{' '}
            <Typography fontWeight={400} fontSize={'0.875rem'}>
              {mainLabel}
            </Typography>
          </>
        )}
      </Label>
    </ChartElement>
  );
};

export default BarElement;
